<script setup>
/**
 * The workroom's top bar: where this ticket sits, what it is called, and the one
 * thing an agent quotes about it — its number.
 *
 * It states and asks, it never acts. The copy affordance emits rather than
 * touching the clipboard, because the screen that owns the store is the only
 * place an alert is raised, and the restore emits the same intent the rail's
 * does — one mutation, offered twice, decided once.
 *
 * The archived banner lives here rather than in the panes: it is a statement
 * about the whole ticket, and it must be the first thing read.
 */
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import Breadcrumb from 'dashboard/components-next/breadcrumb/Breadcrumb.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  // The number agents quote, from the route path.
  displayId: {
    type: [String, Number],
    required: true,
  },
  isArchived: {
    type: Boolean,
    default: false,
  },
  isBusy: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['back', 'copyId', 'unarchive']);

const { t } = useI18n();

// `Tickets › #142` — the product's own word for the surface and its own spelling
// of the number, so the workroom is not the one screen that names them
// differently.
const crumbs = computed(() => [
  { label: t('SUPPORT_CASES.HEADER.TITLE') },
  { label: t('SUPPORT_CASES.LIST.DISPLAY_ID', { id: props.displayId }) },
]);

// Only the ancestor crumb is a way out; the last one is this ticket.
const onCrumbClick = (item, index) => {
  if (index === 0) emit('back');
};
</script>

<template>
  <header class="flex flex-col gap-3 shrink-0" data-testid="case-top-bar">
    <div class="flex flex-wrap items-center gap-2 min-w-0">
      <Button
        variant="link"
        color="slate"
        size="sm"
        icon="i-lucide-arrow-left"
        :label="t('SUPPORT_CASES.DETAIL.BACK')"
        data-testid="case-detail-back"
        @click="emit('back')"
      />

      <Breadcrumb :items="crumbs" class="min-w-0" @click="onCrumbClick" />

      <Button
        variant="ghost"
        color="slate"
        size="xs"
        icon="i-lucide-copy"
        :aria-label="t('SUPPORT_CASES.DETAIL.COPY_ID')"
        data-testid="case-copy-id"
        @click="emit('copyId')"
      />
    </div>

    <div
      v-if="isArchived"
      class="flex flex-wrap items-center gap-3 px-4 py-3 rounded-lg bg-n-alpha-2"
      data-testid="case-archived-banner"
    >
      <span class="i-lucide-archive size-4 shrink-0 text-n-slate-11" />
      <div class="flex flex-col min-w-0">
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('SUPPORT_CASES.DETAIL.ARCHIVED_BANNER.TITLE') }}
        </span>
        <span class="text-xs text-n-slate-11">
          {{ t('SUPPORT_CASES.DETAIL.ARCHIVED_BANNER.SUBTITLE') }}
        </span>
      </div>
      <Button
        size="sm"
        variant="outline"
        color="slate"
        class="ltr:ml-auto rtl:mr-auto"
        :label="t('SUPPORT_CASES.DETAIL.ARCHIVED_BANNER.ACTION')"
        :disabled="isBusy"
        data-testid="case-unarchive-button"
        @click="emit('unarchive')"
      />
    </div>
  </header>
</template>
